import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { CommentData, Project, Shot, Timecode, DrawingTool, MentionUser } from '../types';
import { secondsToTimecode, formatTimecode, timeToFrame, getMaxSeekTime } from '../hooks/useTimecode';
import { useTranslation } from '../hooks/useTranslation';
import VideoPlayer from './VideoPlayer';
import CommentSection from './CommentSection';
import CommentInputBar from './CommentInputBar';
import {
  PlayIcon,
  PauseIcon,
  FullscreenIcon,
  FullscreenExitIcon,
  FramePreviousIcon,
  FrameNextIcon,
  LoopIcon,
  BounceIcon,
  VolumeIcon,
  VolumeMuteIcon,
} from './playerIcons';

interface VideoAsset {
  id: string;
  url?: string;
  file?: File;
}

interface Sequence {
  id: string;
  name: string;
  projectId: string;
}

interface PlayerViewProps {
  asset: VideoAsset;
  shot: Shot;
  project: Project;
  sequence: Sequence | null;
  comments: CommentData[];
  isPlaying: boolean;
  seekTime: number | null;
  isDrawingEnabled: boolean;
  clearDrawingSignal: number;
  undoSignal: number;
  effectiveFps: number;
  drawingTool: DrawingTool;
  drawingColor: string;
  onTimeUpdate: (time: number) => void;
  onPause: () => void;
  onPlay: () => void;
  onDrawingChange: (dataUrl: string | null) => void;
  onSeek: (time: number) => void;
  onDeleteComment: (commentId: string) => void;
  onEditComment?: (commentId: string, newText: string) => void;
  onJumpAndShow: (comment: CommentData) => void;
  onReply?: (parentId: string, text: string, file?: File) => Promise<void>;
  timeInSeconds: number;
  currentTimecode: Timecode;
  onAddComment: (
    text: string,
    recording?: { blob: Blob; duration: number },
    attachment?: File
  ) => void | Promise<void>;
  onToggleDrawing: (enabled: boolean) => void;
  isRecording: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  recordingTime: number;
  setDrawingTool: (tool: DrawingTool) => void;
  setDrawingColor: (color: string) => void;
  onUndo: () => void;
  onClear: () => void;
  currentUserId?: string | null;
  users?: MentionUser[];
  /** Optional external ref to the <video> element (frame capture for comments). */
  videoRef?: React.RefObject<HTMLVideoElement | null>;
}

const isValidAnnotationUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return url.startsWith('data:') || url.startsWith('/');
};

const PlayerView: React.FC<PlayerViewProps> = (props) => {
  const { t } = useTranslation();
  const {
    asset,
    shot,
    comments,
    isPlaying,
    seekTime,
    isDrawingEnabled,
    clearDrawingSignal,
    undoSignal,
    effectiveFps,
    drawingTool,
    drawingColor,
    onTimeUpdate,
    onPause,
    onPlay,
    onDrawingChange,
    onSeek,
    onDeleteComment,
    onEditComment,
    onJumpAndShow,
    onReply,
    timeInSeconds,
    currentTimecode,
    onAddComment,
    onToggleDrawing,
    isRecording,
    onStartRecording,
    onStopRecording,
    recordingTime,
    setDrawingTool,
    setDrawingColor,
    onUndo,
    onClear,
    currentUserId,
    users = [],
    videoRef: externalVideoRef,
  } = props;

  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [playbackMode, setPlaybackMode] = useState<'once' | 'loop' | 'bounce'>('loop');
  const [playbackDirection, setPlaybackDirection] = useState<'forward' | 'backward'>('forward');
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const videoRef = externalVideoRef ?? internalVideoRef;
  const bounceDirectionRef = useRef<'forward' | 'backward'>('forward');

  const isLoopEnabled = playbackMode === 'loop' || playbackMode === 'bounce';
  const isBounceEnabled = playbackMode === 'bounce';

  useEffect(() => {
    setPlaybackDirection('forward');
    bounceDirectionRef.current = 'forward';
  }, [asset?.id]);

  useEffect(() => {
    bounceDirectionRef.current = playbackDirection;
  }, [playbackDirection]);

  // Bounce (ping-pong) — step one frame at fps cadence while reversing
  useEffect(() => {
    if (!isPlaying || !isBounceEnabled) return;
    const fps = effectiveFps || 24;
    const intervalMs = 1000 / fps;
    const id = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || video.paused) return;
      const maxTime = getMaxSeekTime(video.duration || duration, fps);
      const frameDur = 1 / fps;
      if (bounceDirectionRef.current === 'forward') {
        if (video.currentTime >= maxTime - frameDur * 0.5) {
          bounceDirectionRef.current = 'backward';
          setPlaybackDirection('backward');
          video.pause();
          video.currentTime = Math.max(0, maxTime - frameDur);
          video.play().catch(() => {});
        }
      } else {
        const next = Math.max(0, video.currentTime - frameDur);
        video.currentTime = next;
        if (next <= frameDur * 0.5) {
          bounceDirectionRef.current = 'forward';
          setPlaybackDirection('forward');
          video.play().catch(() => {});
        }
        onTimeUpdate(video.currentTime);
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [isPlaying, isBounceEnabled, effectiveFps, duration, onTimeUpdate]);

  const currentAnnotationUrls = useMemo(() => {
    const FRAME_THRESHOLD = 0.1;
    return comments
      .filter((comment) => {
        if (!isValidAnnotationUrl(comment.drawingDataUrl)) return false;
        const commentTime = parseFloat(String(comment.timeInSeconds));
        const currentTimeNum = parseFloat(String(timeInSeconds));
        if (isNaN(commentTime) || isNaN(currentTimeNum)) return false;
        return Math.abs(commentTime - currentTimeNum) <= FRAME_THRESHOLD;
      })
      .map((c) => c.drawingDataUrl!);
  }, [comments, timeInSeconds]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const formattedDuration = useMemo(
    () => formatTimecode(secondsToTimecode(duration, effectiveFps)),
    [duration, effectiveFps]
  );
  const formattedTimecode = useMemo(
    () => formatTimecode(secondsToTimecode(timeInSeconds, effectiveFps)),
    [timeInSeconds, effectiveFps]
  );

  const maxSeekTime = useMemo(
    () => (duration > 0 ? getMaxSeekTime(duration, effectiveFps) : 0),
    [duration, effectiveFps]
  );

  const handleScrubberMouseDown = useCallback(() => {
    setIsScrubbing(true);
  }, []);

  const handleScrubberMouseUp = useCallback(() => {
    setIsScrubbing(false);
  }, []);

  // Release scrub outside the range input
  useEffect(() => {
    if (!isScrubbing) return;
    const end = () => setIsScrubbing(false);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchend', end);
    return () => {
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchend', end);
    };
  }, [isScrubbing]);

  // Mid-frame snap + direct video seek (MagicBox) so scrubber thumb tracks while dragging
  const handleScrubberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawTime = Number(e.target.value);
      const frame = timeToFrame(rawTime, effectiveFps);
      const midFrameTime = (frame + 0.5) / effectiveFps;
      if (videoRef.current) {
        videoRef.current.currentTime = midFrameTime;
      }
      onSeek(midFrameTime);
    },
    [effectiveFps, onSeek, videoRef]
  );

  const handleFrameStep = useCallback(
    (direction: 'forward' | 'backward') => {
      if (isPlaying) onPause();
      const video = videoRef.current;
      const currentVideoTime = video?.currentTime ?? timeInSeconds;
      const currentFrame = timeToFrame(currentVideoTime, effectiveFps);
      const targetFrame = direction === 'forward' ? currentFrame + 1 : currentFrame - 1;
      const maxFrame = Math.max(0, timeToFrame(duration, effectiveFps) - 1);
      const clampedFrame = Math.max(0, Math.min(maxFrame, targetFrame));
      const newTime = (clampedFrame + 0.5) / effectiveFps;
      if (video) {
        video.currentTime = newTime;
      }
      onTimeUpdate(newTime);
      onSeek(newTime);
    },
    [isPlaying, onPause, effectiveFps, duration, timeInSeconds, onSeek, onTimeUpdate, videoRef]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      const isTyping =
        activeEl &&
        (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT');
      if (isTyping) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handleFrameStep('backward');
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleFrameStep('forward');
          break;
        case ' ':
          e.preventDefault();
          isPlaying ? onPause() : onPlay();
          break;
        case 'm':
        case 'M':
          setIsMuted((m) => !m);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleFrameStep, isPlaying, onPause, onPlay]);

  const toggleFullscreen = () => {
    const el = playerContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  };

  const cyclePlaybackMode = () => {
    setPlaybackMode((mode) => {
      if (mode === 'once') return 'loop';
      if (mode === 'loop') return 'bounce';
      return 'once';
    });
    setPlaybackDirection('forward');
    bounceDirectionRef.current = 'forward';
  };

  const playbackModeTitle =
    playbackMode === 'once'
      ? t('player.loopOnce')
      : playbackMode === 'loop'
        ? t('player.loop')
        : t('player.bounce');

  return (
    <main className="flex-1 flex overflow-hidden h-full">
      <div className="flex-1 flex flex-col bg-black relative min-w-0">
        <div
          ref={playerContainerRef}
          className="flex-1 flex justify-center items-center relative overflow-hidden bg-black"
        >
          {asset && asset.url ? (
            <VideoPlayer
              asset={asset}
              onTimeUpdate={onTimeUpdate}
              onPause={onPause}
              onPlay={onPlay}
              isPlaying={isPlaying}
              seekTime={seekTime}
              isDrawingEnabled={isDrawingEnabled}
              onDrawingChange={onDrawingChange}
              clearDrawingSignal={clearDrawingSignal}
              undoSignal={undoSignal}
              fps={effectiveFps}
              annotationUrls={currentAnnotationUrls}
              drawingTool={drawingTool}
              drawingColor={drawingColor}
              onDurationChange={setDuration}
              videoRef={videoRef}
              onToggleFullscreen={toggleFullscreen}
              loop={isLoopEnabled && !isBounceEnabled}
              isReversePlayback={playbackDirection === 'backward'}
              isBounceEnabled={isBounceEnabled}
              muted={isMuted}
              isScrubbing={isScrubbing}
            />
          ) : (
            <div className="text-center text-gray-400">
              <h2 className="text-2xl font-bold text-white mb-2">
                {shot.shotName || `Shot #${shot.shotNumber}`}
              </h2>
              <p>This shot has no playable versions.</p>
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          {asset && asset.url && (
            <div className="bg-gray-800 p-3 text-white border-t border-gray-700">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => (isPlaying ? onPause() : onPlay())}
                  className="text-white hover:text-indigo-400"
                  title={isPlaying ? t('player.pause') : t('player.play')}
                >
                  {isPlaying ? <PauseIcon className="w-8 h-8" /> : <PlayIcon className="w-8 h-8" />}
                </button>
                <button
                  type="button"
                  onClick={() => handleFrameStep('backward')}
                  className="text-white hover:text-indigo-400"
                  title={t('player.framePrev')}
                >
                  <FramePreviousIcon className="w-6 h-6" />
                </button>
                <span className="font-mono text-sm tabular-nums">{formattedTimecode}</span>
                <div className="relative w-full h-2">
                  <div className="absolute top-0 left-0 right-0 h-2 pointer-events-none z-10">
                    {duration > 0 &&
                      comments.map((comment) => {
                        const position = (comment.timeInSeconds / duration) * 100;
                        return (
                          <div
                            key={comment.id}
                            className="absolute top-0 bottom-0 w-1 bg-yellow-400 rounded-sm"
                            style={{ left: `${position}%` }}
                            title={`Comment at ${formatTimecode(comment.timecode)}`}
                          />
                        );
                      })}
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={maxSeekTime}
                    step="any"
                    value={Math.min(timeInSeconds, maxSeekTime || timeInSeconds)}
                    onMouseDown={handleScrubberMouseDown}
                    onMouseUp={handleScrubberMouseUp}
                    onTouchStart={handleScrubberMouseDown}
                    onTouchEnd={handleScrubberMouseUp}
                    onChange={handleScrubberChange}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                </div>
                <span className="font-mono text-sm tabular-nums">{formattedDuration}</span>
                <button
                  type="button"
                  onClick={() => handleFrameStep('forward')}
                  className="text-white hover:text-indigo-400"
                  title={t('player.frameNext')}
                >
                  <FrameNextIcon className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={cyclePlaybackMode}
                  className={`hover:text-indigo-400 ${
                    playbackMode !== 'once' ? 'text-indigo-400' : 'text-white'
                  }`}
                  title={playbackModeTitle}
                >
                  {playbackMode === 'bounce' ? (
                    <BounceIcon className="w-6 h-6" />
                  ) : (
                    <LoopIcon className={`w-6 h-6 ${playbackMode === 'once' ? 'opacity-40' : ''}`} />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setIsMuted((m) => !m)}
                  className="text-white hover:text-indigo-400"
                  title={isMuted ? t('player.unmute') : t('player.mute')}
                >
                  {isMuted ? <VolumeMuteIcon className="w-6 h-6" /> : <VolumeIcon className="w-6 h-6" />}
                </button>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="text-white hover:text-indigo-400"
                  title={isFullscreen ? t('player.exitFullscreen') : t('player.fullscreen')}
                >
                  {isFullscreen ? (
                    <FullscreenExitIcon className="w-6 h-6" />
                  ) : (
                    <FullscreenIcon className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>
          )}
          <CommentInputBar
            onAddComment={onAddComment}
            currentTimecode={currentTimecode}
            isDrawingEnabled={isDrawingEnabled}
            onToggleDrawing={onToggleDrawing}
            isRecording={isRecording}
            onStartRecording={onStartRecording}
            onStopRecording={onStopRecording}
            recordingTime={recordingTime}
            drawingTool={drawingTool}
            setDrawingTool={setDrawingTool}
            drawingColor={drawingColor}
            setDrawingColor={setDrawingColor}
            onUndo={onUndo}
            onClear={onClear}
            users={users}
          />
        </div>
      </div>
      <aside className="w-[420px] flex-shrink-0 border-l border-gray-700 flex flex-col">
        <CommentSection
          comments={comments}
          onSeek={onSeek}
          onDeleteComment={onDeleteComment}
          onEditComment={onEditComment}
          onJumpAndShow={onJumpAndShow}
          onReply={onReply}
          currentUserId={currentUserId}
          fps={effectiveFps}
          users={users}
        />
      </aside>
    </main>
  );
};

export default PlayerView;
